import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import {
  APIProvider,
  Map,
  AdvancedMarker,
  AdvancedMarkerAnchorPoint,
  useMap,
} from '@vis.gl/react-google-maps';
import { useDevice, useOptimisticList } from '@/hooks';
import { geofenceService } from '@/api/services';
import type { NAGlobalGeofence } from '@/api/models';
import { Button } from '@/components/common/Button/Button';
import { Modal } from '@/components/common/Modal/Modal';
import { EmptyState } from '@/components/common/EmptyState/EmptyState';
import { StatusBadge } from '@/components/common/StatusBadge/StatusBadge';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import {
  formatCoordinate,
  formatFeet,
  feetToMeters,
  formatDateTime,
} from '@/lib/formatters';
import { MAX_GEOFENCES } from '@/config/constants';
import { env } from '@/config/env';

// Minimum geofence radius in feet
const MIN_GEOFENCE_RADIUS_FT = 50;

// ---------------------------------------------------------------------------
// GeofenceCircles -- draws google.maps.Circle objects
// ---------------------------------------------------------------------------
function GeofenceCircles({
  geofences,
  selectedKey,
  getKey,
  onSelect,
}: {
  geofences: NAGlobalGeofence[];
  selectedKey: string | null;
  getKey: (gf: NAGlobalGeofence) => string;
  onSelect: (key: string) => void;
}) {
  const map = useMap();
  const circlesRef = useRef<google.maps.Circle[]>([]);

  useEffect(() => {
    circlesRef.current.forEach((c) => {
      google.maps.event.clearInstanceListeners(c);
      c.setMap(null);
    });
    circlesRef.current = [];
    if (!map) return;

    geofences.forEach((gf) => {
      // Not colored by isDeviceSynced/outsideGeofence -- that sync-state data
      // hasn't proven reliable, so every geofence gets the same neutral color
      // regardless of reported status; only selection changes the color.
      const color = '#94A3B8';
      const isSelected = getKey(gf) === selectedKey;
      const circle = new google.maps.Circle({
        center: { lat: gf.refLat, lng: gf.refLon },
        radius: feetToMeters(gf.radius),
        fillColor: color,
        fillOpacity: isSelected ? 0.25 : 0.15,
        strokeColor: isSelected ? '#0D9488' : color,
        strokeOpacity: isSelected ? 1 : 0.6,
        strokeWeight: isSelected ? 4 : 2,
        zIndex: isSelected ? 10 : 1,
        clickable: true,
        map,
      });
      circle.addListener('click', () => onSelect(getKey(gf)));
      circlesRef.current.push(circle);
    });

    return () => {
      circlesRef.current.forEach((c) => {
        google.maps.event.clearInstanceListeners(c);
        c.setMap(null);
      });
      circlesRef.current = [];
    };
  }, [map, geofences, selectedKey, getKey, onSelect]);

  return null;
}

// ---------------------------------------------------------------------------
// AutoFitBounds
// ---------------------------------------------------------------------------
function AutoFitBounds({
  geofences,
  deviceLat,
  deviceLng,
}: {
  geofences: NAGlobalGeofence[];
  deviceLat: number | null;
  deviceLng: number | null;
}) {
  const map = useMap();

  useEffect(() => {
    if (!map) return;
    const bounds = new google.maps.LatLngBounds();
    let hasPoints = false;

    geofences.forEach((gf) => {
      const radiusM = feetToMeters(gf.radius);
      const dLat = radiusM / 111320;
      const dLng = radiusM / (111320 * Math.cos((gf.refLat * Math.PI) / 180));
      bounds.extend({ lat: gf.refLat + dLat, lng: gf.refLon + dLng });
      bounds.extend({ lat: gf.refLat - dLat, lng: gf.refLon - dLng });
      hasPoints = true;
    });

    if (deviceLat != null && deviceLng != null) {
      bounds.extend({ lat: deviceLat, lng: deviceLng });
      hasPoints = true;
    }

    if (!hasPoints) return;

    if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
      const center = bounds.getCenter();
      bounds.extend({ lat: center.lat() + 0.005, lng: center.lng() + 0.005 });
      bounds.extend({ lat: center.lat() - 0.005, lng: center.lng() - 0.005 });
    }

    map.fitBounds(bounds, { top: 30, right: 30, bottom: 30, left: 30 });
  }, [map, geofences, deviceLat, deviceLng]);

  return null;
}

// ---------------------------------------------------------------------------
// FocusOnSelectedGeofence -- pans/zooms the map to fit a selected geofence
// ---------------------------------------------------------------------------
function FocusOnSelectedGeofence({ geofence }: { geofence: NAGlobalGeofence | null }) {
  const map = useMap();
  // Depend on primitives, not the geofence object identity -- the list's
  // object references change on every poll refresh (isDeviceSynced /
  // outsideGeofence updates), which would otherwise re-fit the map and yank
  // the user's view back to the selected geofence on every poll tick.
  const lat = geofence?.refLat;
  const lng = geofence?.refLon;
  const radius = geofence?.radius;

  useEffect(() => {
    if (!map || lat == null || lng == null || radius == null) return;

    const radiusM = feetToMeters(radius);
    const dLat = radiusM / 111320;
    const dLng = radiusM / (111320 * Math.cos((lat * Math.PI) / 180));
    const bounds = new google.maps.LatLngBounds();
    bounds.extend({ lat: lat + dLat, lng: lng + dLng });
    bounds.extend({ lat: lat - dLat, lng: lng - dLng });

    map.fitBounds(bounds, { top: 60, right: 60, bottom: 60, left: 60 });
  }, [map, lat, lng, radius]);

  return null;
}

// ---------------------------------------------------------------------------
// AddGeofenceMapCircle -- preview circle in add modal
// ---------------------------------------------------------------------------
function AddGeofenceMapCircle({
  center,
  radiusFt,
}: {
  center: google.maps.LatLngLiteral | null;
  radiusFt: number;
}) {
  const map = useMap();
  const circleRef = useRef<google.maps.Circle | null>(null);

  useEffect(() => {
    circleRef.current?.setMap(null);
    circleRef.current = null;
    if (!map || !center || !radiusFt || radiusFt < MIN_GEOFENCE_RADIUS_FT) return;

    circleRef.current = new google.maps.Circle({
      center,
      radius: feetToMeters(radiusFt),
      fillColor: '#14B8A6',
      fillOpacity: 0.15,
      strokeColor: '#14B8A6',
      strokeOpacity: 0.6,
      strokeWeight: 2,
      map,
    });

    return () => {
      circleRef.current?.setMap(null);
      circleRef.current = null;
    };
  }, [map, center, radiusFt]);

  return null;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export function GlobalGeofencesPage() {
  const { selectedDeviceInfo, selectedDevice, deviceInfoLoading } = useDevice();
  const deviceUUID = selectedDevice?.deviceProperties.deviceUUID ?? '';

  const [rawGeofences, setRawGeofences] = useState<NAGlobalGeofence[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Optimistic add/remove keyed by name — geofenceId is server-assigned and
  // not known at add time, so name (required, entered by the user) is the
  // one identifier stable across the optimistic placeholder and the eventual
  // server row. Same pattern as ContactListPage, since this list can go
  // briefly stale immediately after a write just like the polled ones do.
  const geofenceKey = useCallback((gf: NAGlobalGeofence) => gf.name, []);
  const { mergedList: geofences, addOptimistic, removeOptimistic } = useOptimisticList(rawGeofences, geofenceKey);

  // Selection, shared between the list and the map — same identity key used
  // by the list/marker `key` props elsewhere in this file.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const getSelectKey = useCallback((gf: NAGlobalGeofence) => String(gf.geofenceId ?? gf.name), []);

  // Add modal
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', lat: '', lon: '', radius: '' });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState('');

  // Delete modal
  const [deleteTarget, setDeleteTarget] = useState<NAGlobalGeofence | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const globalGeofencingEnabled =
    selectedDeviceInfo?.deviceSubscription?.globalGeofencingEnabled ?? false;

  // The standalone /Geofence/List response's per-item outsideGeofence hasn't
  // proven reliable, so the inside/outside indicator is sourced from
  // DeviceInfo's own sensorData.globalGeofenceList instead -- same underlying
  // DB row, same field, matched by geofenceId. (A plain object, not a Map --
  // `Map` here would shadow the react-google-maps `Map` component import.)
  const deviceGeofenceStatusByKey: Record<string, boolean | undefined> = {};
  for (const gf of selectedDeviceInfo?.sensorData?.globalGeofenceList ?? []) {
    deviceGeofenceStatusByKey[getSelectKey(gf)] = gf.outsideGeofence;
  }

  const fetchGeofences = useCallback(async () => {
    if (!deviceUUID || !globalGeofencingEnabled) return;
    setLoading(true);
    setError('');
    try {
      const resp = await geofenceService.getGlobalGeofenceList(deviceUUID);
      setRawGeofences(resp ?? []);
    } catch (err: unknown) {
      // A device that has never had a geofence added has no "no entries"
      // response shape distinct from other failures — treat any list-fetch
      // failure as "no geofences yet" rather than surfacing a scary error
      // banner. The empty state below already offers an Add Geofence action,
      // and a real outage would also fail the add/delete actions (which do
      // still show their own error banners).
      console.error('Failed to load global geofences', err);
      setRawGeofences([]);
    } finally {
      setLoading(false);
    }
  }, [deviceUUID, globalGeofencingEnabled]);

  useEffect(() => {
    fetchGeofences();
  }, [fetchGeofences]);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError('');

    const lat = parseFloat(addForm.lat);
    const lon = parseFloat(addForm.lon);
    const radius = parseFloat(addForm.radius);

    if (!addForm.name.trim()) { setAddError('Name is required.'); return; }
    if (isNaN(lat) || lat < -90 || lat > 90) { setAddError('Latitude must be between -90 and 90.'); return; }
    if (isNaN(lon) || lon < -180 || lon > 180) { setAddError('Longitude must be between -180 and 180.'); return; }
    if (isNaN(radius) || radius < MIN_GEOFENCE_RADIUS_FT) { setAddError(`Radius must be at least ${MIN_GEOFENCE_RADIUS_FT} ft.`); return; }

    setAddLoading(true);
    try {
      const name = addForm.name.trim();
      await geofenceService.addGlobalGeofence({
        deviceUUID,
        geoName: name,
        lat,
        lon,
        radius,
      });
      addOptimistic({ name, refLat: lat, refLon: lon, radius });
      setShowAddModal(false);
      setAddForm({ name: '', lat: '', lon: '', radius: '' });
      await fetchGeofences();
    } catch {
      setAddError('Failed to add geofence.');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await geofenceService.deleteGlobalGeofence({
        deviceUUID,
        geofenceId: deleteTarget.geofenceId!,
      });
      removeOptimistic(geofenceKey(deleteTarget));
      setDeleteTarget(null);
      await fetchGeofences();
    } catch {
      setError('Failed to delete geofence.');
    } finally {
      setDeleteLoading(false);
    }
  };

  if (deviceInfoLoading && !selectedDeviceInfo) return <PageSpinner />;

  if (!selectedDeviceInfo) {
    return <EmptyState title="No Device Selected" description="Select a device to view global geofences." />;
  }

  // Subscription check
  if (!globalGeofencingEnabled) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Global Geofences</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            User-created geofence boundaries for your vessel
          </p>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-6 dark:border-blue-800/30 dark:bg-blue-900/10">
          <div className="flex items-start gap-3">
            <svg className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-300">Subscription Upgrade Required</h3>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-400">
                Global Geofencing requires a subscription upgrade. Global geofences allow you to define multiple
                geographic boundaries and receive notifications when your vessel enters or exits each area.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentGPS = selectedDeviceInfo?.gpsLocationData;
  const hasMapData = geofences.length > 0 || currentGPS?.locationValid;

  const defaultCenter =
    geofences.length > 0
      ? { lat: geofences[0].refLat, lng: geofences[0].refLon }
      : currentGPS?.locationValid
        ? { lat: currentGPS.latitude, lng: currentGPS.longitude }
        : { lat: 25.7617, lng: -80.1918 };

  return (
    <div className="flex h-full flex-col space-y-0">
      {/* Header */}
      <div className="flex items-center justify-between px-1 pb-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Global Geofences</h1>
          <p className="mt-0.5 text-sm text-slate-500 dark:text-slate-400">
            User-created geofence boundaries for your vessel
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500 dark:text-slate-400">
            {geofences.length} of {MAX_GEOFENCES} geofences
          </span>
          <Button onClick={() => setShowAddModal(true)} disabled={geofences.length >= MAX_GEOFENCES}>
            Add Geofence
          </Button>
        </div>
      </div>

      {error && (
        <div className="mx-1 mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
          {error}
        </div>
      )}

      {/* Map */}
      <div className="mb-4 overflow-hidden rounded-xl border border-slate-200 dark:border-navy-700">
        {!hasMapData ? (
          <div className="flex h-[300px] items-center justify-center bg-slate-100 dark:bg-navy-900">
            <EmptyState title="No geofences" description="Add a geofence to see it on the map." />
          </div>
        ) : (
          <APIProvider apiKey={env.GOOGLE_MAPS_API_KEY}>
            <Map
              mapId="global-geofences-map"
              defaultCenter={defaultCenter}
              defaultZoom={14}
              style={{ height: 'calc(60vh - 120px)', minHeight: '280px', width: '100%' }}
              gestureHandling="greedy"
              disableDefaultUI={false}
              mapTypeId="hybrid"
            >
              <AutoFitBounds
                geofences={geofences}
                deviceLat={currentGPS?.locationValid ? currentGPS.latitude : null}
                deviceLng={currentGPS?.locationValid ? currentGPS.longitude : null}
              />
              <FocusOnSelectedGeofence geofence={geofences.find((gf) => getSelectKey(gf) === selectedKey) ?? null} />
              <GeofenceCircles geofences={geofences} selectedKey={selectedKey} getKey={getSelectKey} onSelect={setSelectedKey} />

              {/* Center markers -- dot is a separate CENTER-anchored marker so it
                  sits exactly on the geofence's true center point; the name
                  label is a second marker anchored just below it, so the
                  label's height never pulls the dot off-center. */}
              {geofences.map((gf, idx) => {
                // Not colored by isDeviceSynced/outsideGeofence -- see GeofenceCircles.
                const dotColor = '#94A3B8';
                const key = getSelectKey(gf) || `gf-${idx}`;
                const isSelected = key === selectedKey;
                return (
                  <Fragment key={`gf-marker-${key}`}>
                    <AdvancedMarker
                      position={{ lat: gf.refLat, lng: gf.refLon }}
                      zIndex={isSelected ? 200 : 100}
                      anchorPoint={AdvancedMarkerAnchorPoint.CENTER}
                      onClick={() => setSelectedKey(key)}
                    >
                      <div
                        style={{
                          width: isSelected ? 14 : 10,
                          height: isSelected ? 14 : 10,
                          borderRadius: '50%',
                          backgroundColor: dotColor,
                          border: '1.5px solid #fff',
                          boxShadow: isSelected
                            ? '0 0 0 4px rgba(13,148,136,0.35), 0 1px 2px rgba(0,0,0,0.3)'
                            : '0 1px 2px rgba(0,0,0,0.3)',
                          cursor: 'pointer',
                        }}
                      />
                    </AdvancedMarker>
                    <AdvancedMarker
                      position={{ lat: gf.refLat, lng: gf.refLon }}
                      zIndex={isSelected ? 200 : 100}
                      anchorPoint={AdvancedMarkerAnchorPoint.TOP}
                      onClick={() => setSelectedKey(key)}
                    >
                      <span
                        className={`mt-1.5 block whitespace-nowrap rounded px-1 py-0.5 text-[10px] font-medium shadow-sm ${
                          isSelected
                            ? 'bg-teal-600 text-white dark:bg-teal-500'
                            : 'bg-white/90 text-slate-700 dark:bg-navy-800/90 dark:text-slate-300'
                        }`}
                        style={{ cursor: 'pointer' }}
                      >
                        {gf.name}
                      </span>
                    </AdvancedMarker>
                  </Fragment>
                );
              })}

              {/* Vessel position */}
              {currentGPS?.locationValid && (
                <AdvancedMarker position={{ lat: currentGPS.latitude, lng: currentGPS.longitude }} zIndex={1000}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: '#06B6D4', border: '3px solid #fff', boxShadow: '0 2px 6px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#fff' }} />
                  </div>
                </AdvancedMarker>
              )}
            </Map>
          </APIProvider>
        )}
      </div>

      {/* List */}
      <div className="flex-1 space-y-3 overflow-y-auto">
        {loading ? (
          <PageSpinner />
        ) : geofences.length === 0 ? (
          <EmptyState
            title="No geofences configured"
            description="Add a geofence to receive alerts when your vessel moves outside the boundary."
            action={<Button onClick={() => setShowAddModal(true)}>Add Geofence</Button>}
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {geofences.map((gf) => {
              const key = getSelectKey(gf);
              const isSelected = key === selectedKey;
              // Being outside a boundary is expected/normal for a vessel
              // underway, not an alert -- green when inside, grey when
              // outside (not the red/neutral alarm-tile convention).
              const isOutside = deviceGeofenceStatusByKey[key] === true;
              const tintBg = isOutside ? 'bg-slate-50 dark:bg-navy-800' : 'bg-green-50 dark:bg-green-900/10';
              const tintBorder = isOutside
                ? 'border-slate-300 dark:border-navy-600'
                : 'border-green-400 dark:border-green-600';
              return (
              <div
                key={key}
                onClick={() => setSelectedKey(key)}
                className={`cursor-pointer rounded-xl border p-5 transition-colors ${tintBg} ${
                  isSelected
                    ? 'border-teal-500 ring-1 ring-teal-500 dark:border-teal-400 dark:ring-teal-400'
                    : `${tintBorder} hover:border-slate-300 dark:hover:border-navy-600`
                }`}
              >
                <div className="mb-3 flex items-center justify-between gap-2">
                  <div className="flex min-w-0 flex-1 items-center gap-2">
                    <h3 className="min-w-0 truncate font-bold text-slate-800 dark:text-white">{gf.name}</h3>
                    <div className="flex-shrink-0">
                      <StatusBadge label={isOutside ? 'Outside' : 'Inside'} variant={isOutside ? 'neutral' : 'success'} dot />
                    </div>
                  </div>
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={gf.geofenceId == null}
                    onClick={(e) => {
                      e.stopPropagation();
                      setDeleteTarget(gf);
                    }}
                  >
                    Delete
                  </Button>
                </div>
                <div className="space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400">Center</span>
                    <span className="font-mono text-slate-700 dark:text-slate-300">{formatCoordinate(gf.refLat, gf.refLon)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500 dark:text-slate-400">Radius</span>
                    <span className="text-slate-700 dark:text-slate-300">{formatFeet(gf.radius)}</span>
                  </div>
                  {gf.createdDT && (
                    <div className="flex justify-between">
                      <span className="text-slate-500 dark:text-slate-400">Created</span>
                      <span className="text-slate-700 dark:text-slate-300">{formatDateTime(gf.createdDT)}</span>
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Geofence Modal */}
      <Modal isOpen={showAddModal} onClose={() => setShowAddModal(false)} title="Add Geofence" size="xl">
        <form onSubmit={handleAdd} className="space-y-4">
          {addError && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
              {addError}
            </div>
          )}

          {/* Map for pin placement */}
          <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-navy-700">
            <p className="bg-slate-50 px-3 py-1.5 text-xs text-slate-500 dark:bg-navy-800 dark:text-slate-400">
              Click on the map to set the geofence center
            </p>
            <APIProvider apiKey={env.GOOGLE_MAPS_API_KEY}>
              <Map
                mapId="add-geofence-map"
                defaultCenter={
                  currentGPS?.locationValid
                    ? { lat: currentGPS.latitude, lng: currentGPS.longitude }
                    : { lat: 25.76, lng: -80.19 }
                }
                defaultZoom={14}
                style={{ height: '300px', width: '100%' }}
                gestureHandling="greedy"
                disableDefaultUI={false}
                mapTypeId="hybrid"
                onClick={(e) => {
                  const latLng = e.detail.latLng;
                  if (latLng) {
                    setAddForm((f) => ({ ...f, lat: latLng.lat.toFixed(6), lon: latLng.lng.toFixed(6) }));
                  }
                }}
              >
                {addForm.lat && addForm.lon && !isNaN(parseFloat(addForm.lat)) && !isNaN(parseFloat(addForm.lon)) && (
                  <>
                    <AdvancedMarker position={{ lat: parseFloat(addForm.lat), lng: parseFloat(addForm.lon) }} zIndex={100}>
                      <div style={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#14B8A6', border: '2.5px solid #fff', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }} />
                    </AdvancedMarker>
                    <AddGeofenceMapCircle center={{ lat: parseFloat(addForm.lat), lng: parseFloat(addForm.lon) }} radiusFt={parseFloat(addForm.radius) || 0} />
                  </>
                )}
              </Map>
            </APIProvider>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Name</label>
            <input type="text" value={addForm.name} onChange={(e) => setAddForm((f) => ({ ...f, name: e.target.value }))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white" placeholder="My Marina" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Latitude</label>
              <input type="number" step="any" value={addForm.lat} onChange={(e) => setAddForm((f) => ({ ...f, lat: e.target.value }))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white" placeholder="26.1234" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Longitude</label>
              <input type="number" step="any" value={addForm.lon} onChange={(e) => setAddForm((f) => ({ ...f, lon: e.target.value }))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white" placeholder="-80.1234" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">Radius (feet, min {MIN_GEOFENCE_RADIUS_FT})</label>
            <input type="number" value={addForm.radius} onChange={(e) => setAddForm((f) => ({ ...f, radius: e.target.value }))} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500 dark:border-navy-600 dark:bg-navy-800 dark:text-white" placeholder="150" min={MIN_GEOFENCE_RADIUS_FT} />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="secondary" type="button" onClick={() => setShowAddModal(false)}>Cancel</Button>
            <Button type="submit" loading={addLoading}>Add Geofence</Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={!!deleteTarget} onClose={() => setDeleteTarget(null)} title="Delete Geofence">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Are you sure you want to delete the geofence{' '}
          <strong className="text-slate-800 dark:text-white">{deleteTarget?.name}</strong>? This action cannot be undone.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button variant="danger" loading={deleteLoading} onClick={handleDelete}>Delete</Button>
        </div>
      </Modal>
    </div>
  );
}
